import React, { useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import { isAfter, isBefore } from 'date-fns'

import { useSharedDates } from '@contexts/sharedDates'
import { useAddTaskComment, useFindTaskComments } from '@hooks/comments'
import { useDate } from '@hooks/dates'
import { useRoles } from '@hooks/roles'
import { useFindTask } from '@hooks/task/find'

import { TaskCard } from './Card'
import { CommentsModal } from '../../components/Comments'
import { Section } from '../../components/Section'

export function TaskSection() {
  const params = useParams()
  const taskId = Number(params.taskId)

  const navigate = useNavigate()

  const { selectedDate } = useSharedDates()
  const { getWeekRange } = useDate()

  const { mutateAsync: postTaskComment } = useAddTaskComment()

  const { readOnlyUserRoles, adminRoles, isUserAllowed } = useRoles()
  const isReadOnlyUser = isUserAllowed(readOnlyUserRoles)
  const isAdmin = isUserAllowed(adminRoles)

  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false)

  const { data: rawTask } = useFindTask(taskId)
  const { data: rawComments } = useFindTaskComments(taskId)
  const comments = rawComments ?? []

  const { weekStart, weekEnd } = getWeekRange(new Date())
  const isEditable = isBefore(selectedDate, weekStart)

  const task = rawTask ?? ({} as NonNullable<typeof rawTask>)

  const isSelectedDateAhead = isAfter(selectedDate, weekEnd)

  const isAlreadyReset = !!task.isReset
  const isISTeTask = !!task.code

  const scheduleFeedbackTitle = isSelectedDateAhead ? 'Pull Ahead' : 'Reset'

  const taskActions = [
    {
      title: 'Add Task',
      isDisabled: isReadOnlyUser,
      onPress: () =>
        navigate(`/task/${taskId}/add-task`, {
          state: { jobId: task.jobId },
        }),
    },
    ...(!isAlreadyReset && isAdmin
      ? [
          {
            title: scheduleFeedbackTitle,
            isDisabled: isEditable || !isISTeTask,
            onPress: () =>
              navigate(`/task/${taskId}/reset-pull-ahead`),
          },
        ]
      : []),
    {
      title: 'Edit',
      isDisabled: isEditable || isReadOnlyUser,
      onPress: () =>
        navigate(`/task/${taskId}/edit`),
    },
  ]

  const handleCloseCommentsModal = () => {
    setIsCommentsModalOpen(false)
  }

  const handleCreateComment = (comment: string) => {
    postTaskComment({
      comment,
      taskId,
      isSapComment: false,
    })
  }

  return (
    <Section title="Task Details" actions={taskActions}>
      <TaskCard onCommentIconClick={() => setIsCommentsModalOpen(true)} />

      <CommentsModal
        openModal={isCommentsModalOpen}
        comments={comments}
        handleComment={handleCreateComment}
        onClose={handleCloseCommentsModal}
        isReadOnly={isReadOnlyUser}
      />
    </Section>
  )
}
